import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Stack,
  Switch,
  TextareaAutosize,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import { MemoryBook } from "../../../../../types/memory-table";
import { activeTabType } from "../MemoryTableView2";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";


interface Props {
  setActiveTab: React.Dispatch<React.SetStateAction<activeTabType>>;
}

export const MemoryBookDelivery: React.FC<Props> = ({ setActiveTab }) => {
  const { id } = useParams();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  //const reduxTheme = useSelector((state: RootState) => state.auth.user.);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [deliveredTo, setDeliveredTo] = useState<string>("");
  const [deliveredBy, setDeliveredBy] = useState<string>("");
  const [courierName, setCourierName] = useState<string>("");
  const [deliverySwitchChecked, setDeliverySwitchChecked] =
    useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [deliverOptions, setDeliverOptions] = useState<string[]>([]);
  const [deliveryNoteFile, setDeliveryNoteFile] = useState<File | null>(null);
  const [deliveryNotePreviewUrl, setDeliveryNotePreviewUrl] = useState<
    string | null
  >(null);
  const [existingDeliveryNoteUrl, setExistingDeliveryNoteUrl] = useState<
    string | null
  >(null);

  const fetchMemoryBooks = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${id}`,
      );

      if (response.data.results.length > 0) {
        const memoryBook = response.data.results[0];
        setDeliverySwitchChecked(memoryBook.is_delivered);
        setNotes(memoryBook.delevery_notes || memoryBook.notes || "");
        setCourierName(memoryBook.courier_name || "");
        setDeliveredTo(memoryBook.delivered_to || "");
        setDeliveredBy(memoryBook.delivered_by || "");
        setEmail(memoryBook.delivery_email || "");
        setExistingDeliveryNoteUrl(memoryBook.delivery_note_file || null);
        const nextOptions: string[] = [];
        if (memoryBook.courier_name) {
          nextOptions.push("courier");
        }
        if (memoryBook.delivered_to) {
          nextOptions.push("in_person");
        }
        if (memoryBook.delivery_email) {
          nextOptions.push("email");
        }
        setDeliverOptions(nextOptions);
      }

      setMemoryBooks(response.data.results);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load memory books. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMemoryBooks();
  }, [fetchMemoryBooks]);

  const updateDeliveryDetails = async () => {
    setIsSubmitting(true);

    // data integrity checks
    if (deliverOptions.length === 0 && deliverySwitchChecked) {
      showErrorToast("You must select at least one delivery method.");
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("email") && email.length === 0) {
      showErrorToast(
        "You selected email as a delivery method but did not provide an email address.",
      );
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("in_person") && deliveredTo.length === 0) {
      showErrorToast(
        "You selected in-person delivery but did not provide a delivery to name.",
      );
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("courier") && courierName.length === 0) {
      showErrorToast(
        "You selected courier delivery but did not provide a courier name.",
      );
      setIsSubmitting(false);
      return;
    }

    //

    try {
      const currentBook: MemoryBook = memoryBooks[0];

      const formData = new FormData();
      formData.append("is_delivered", `${deliverySwitchChecked}`);
      formData.append("delivery_method", JSON.stringify(deliverOptions));
      formData.append("delevery_notes", notes);

      if (deliveryNoteFile) {
        formData.append("delivery_note_file", deliveryNoteFile);
      }

      // add data based on checked options
      if (deliverOptions.includes("email")) {
        formData.append("delivery_email", email);
      }

      if (
        deliverOptions.includes("in_person") ||
        deliverOptions.includes("courier")
      ) {
        formData.append("delivered_to", deliveredTo);
        formData.append("delivered_by", deliveredBy);
      }

      if (deliverOptions.includes("courier")) {
        formData.append("courier_name", courierName);
      }

      // resetting data based on unchecked options

      if (
        !deliverOptions.includes("in_person") &&
        !deliverOptions.includes("courier")
      ) {
        formData.append("delivered_to", "");
        formData.append("delivered_by", "");
      }

      if (!deliverOptions.includes("courier")) {
        formData.append("courier_name", "");
      }

      if (!deliverOptions.includes("email")) {
        formData.append("delivery_email", "");
      }

      const response = await apiService.patch<MemoryBook>(
        `/api/streams/memory-book/${currentBook.id}/`,
        formData,
      );

      if (deliveryNoteFile) {
        setExistingDeliveryNoteUrl(response.data.delivery_note_file || null);
        if (deliveryNotePreviewUrl) URL.revokeObjectURL(deliveryNotePreviewUrl);
        setDeliveryNoteFile(null);
        setDeliveryNotePreviewUrl(null);
      }

      toast.success("Detailed updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to update details.Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeliveryOptionChange = (option: string) => {
    setDeliverOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const handleDeliveredToggle = () => {
    if (deliverySwitchChecked) {
      setDeliverOptions([]);
      setEmail("");
      setDeliveredBy("");
      setCourierName("");
      setDeliveredTo("");
    }
    setDeliverySwitchChecked(!deliverySwitchChecked);
  };

  const [isDraggingOverDeliveryNote, setIsDraggingOverDeliveryNote] =
    useState<boolean>(false);

  const applyDeliveryNoteFile = (file: File) => {
    setDeliveryNoteFile(file);
    if (file.type.startsWith("image/")) {
      setDeliveryNotePreviewUrl(URL.createObjectURL(file));
    } else {
      setDeliveryNotePreviewUrl(null);
    }
  };

  const handleDeliveryNoteSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    applyDeliveryNoteFile(file);
  };

  const handleDeliveryNoteDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOverDeliveryNote(true);
  };

  const handleDeliveryNoteDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOverDeliveryNote(false);
  };

  const handleDeliveryNoteDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOverDeliveryNote(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    applyDeliveryNoteFile(file);
  };

  const handleRemoveDeliveryNote = () => {
    if (deliveryNotePreviewUrl) URL.revokeObjectURL(deliveryNotePreviewUrl);
    setDeliveryNoteFile(null);
    setDeliveryNotePreviewUrl(null);
  };

  useEffect(() => {
    return () => {
      if (deliveryNotePreviewUrl) URL.revokeObjectURL(deliveryNotePreviewUrl);
    };
  }, [deliveryNotePreviewUrl]);

  return (
    <Box p={2} borderRadius={2} bgcolor={colors.primary[900]}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
          }}
        >
          <CircularProgress size={25} />
        </Box>
      )}

      {error && !isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
            flexDirection: "column",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1, fontSize: 13 }}>
            Failed to fetch memory book
          </Typography>
          <Button variant="contained" onClick={fetchMemoryBooks}>
            Retry
          </Button>
        </Box>
      )}

      {!error && !isLoading && memoryBooks.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
            flexDirection: "column",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1, fontSize: 13 }}>
            No memory book has been upload yet.Please upload one to access this
            menu
          </Typography>
          <Button variant="contained" onClick={() => setActiveTab("upload")}>
            Upload
          </Button>
        </Box>
      )}
      {!error && !isLoading && memoryBooks.length > 0 && (
        <>
          <Grid container spacing={2} mt={3} mb={2}>
            <Grid
              size={6}
              sx={{
                border: `1px solid ${colors.primary[800]}`,
                //height: 200,
                borderRadius: 3,
                padding: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: ".5em",
                  bgcolor: colors.primary[800],
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Delivered
                </Typography>

                <Switch
                  checked={deliverySwitchChecked}
                  onClick={handleDeliveredToggle}
                />
              </Box>

              <Stack direction={"row"} spacing={5} sx={{ mt: 2 }}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      onClick={() => handleDeliveryOptionChange("email")}
                      checked={deliverOptions.includes("email")}
                    />
                    <Typography variant="body1">Email</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    onClick={() => handleDeliveryOptionChange("in_person")}
                    checked={deliverOptions.includes("in_person")}
                  />
                  <Typography variant="body1">In person</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    onClick={() => handleDeliveryOptionChange("courier")}
                    checked={deliverOptions.includes("courier")}
                  />
                  <Typography variant="body1">Courier</Typography>
                </Box>
              </Stack>

              {deliverOptions.includes("email") && (
                <TextField
                  required
                  value={email}
                  id="outlined-required"
                  label="Email"
                  placeholder="Enter email you delivered to"
                  sx={{ mt: 2, width: "100%" }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}

              {(deliverOptions.includes("in_person") ||
                deliverOptions.includes("courier")) && (
                <>
                  <TextField
                    required
                    value={deliveredBy}
                    id="outlined-required"
                    label="Delivered by"
                    placeholder="Enter name of person who delivered"
                    sx={{ mt: 2, width: "100%" }}
                    onChange={(e) => setDeliveredBy(e.target.value)}
                  />

                  <TextField
                    required
                    value={deliveredTo}
                    id="outlined-required"
                    label="Delivered to"
                    placeholder="Enter name of person you delivered to"
                    sx={{ mt: 2, width: "100%" }}
                    onChange={(e) => setDeliveredTo(e.target.value)}
                  />
                </>
              )}

              {deliverOptions.includes("courier") && (
                <TextField
                  required
                  value={courierName}
                  id="outlined-required"
                  label="Courier Name"
                  placeholder="Enter name of the courier you hired"
                  sx={{ mt: 2, width: "100%" }}
                  onChange={(e) => setCourierName(e.target.value)}
                />
              )}

              {/* <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id="delivery-method-select">
                  Delivery Method
                </InputLabel>
                <Select
                  labelId="delivery-method-select"
                  id="delivery-method-select"
                  value={deliveryMethod}
                  label="Delivery Method"
                  onChange={handleChange}
                >
                  <MenuItem value={"email"}>Email</MenuItem>
                  <MenuItem value={"in-person"}>In person</MenuItem>
                  <MenuItem value={"courier"}>Courier</MenuItem>
                </Select>
              </FormControl> */}

              <Box
                sx={{
                  padding: ".5em",
                  border: `1px solid ${colors.primary[800]}`,
                  mt: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontSize: 10, mb: 1 }}>
                  Delivered by
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  { deliveredBy || courierName || email || "Not specified"}
                </Typography>
              </Box>
            </Grid>

            <Grid size={6}>
              <TextareaAutosize
                onChange={(e) => setNotes(e.target.value)}
                value={notes}
                aria-placeholder="Add any special instructions or notes"
                placeholder="Add any special instructions or notes"
                style={{
                  width: "100%",
                  resize: "none",
                  padding: "0.5rem",
                  height: 234,
                  maxHeight: 234,
                  borderRadius: 4,
                  backgroundColor: colors.primary[800],
                  border: "none",
                }}
              />

              <Box
                onDragOver={handleDeliveryNoteDragOver}
                onDragLeave={handleDeliveryNoteDragLeave}
                onDrop={handleDeliveryNoteDrop}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: `1px dashed ${
                    isDraggingOverDeliveryNote
                      ? colors.primary[400]
                      : colors.primary[700]
                  }`,
                  bgcolor: isDraggingOverDeliveryNote
                    ? colors.primary[800]
                    : "transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 1.5, color: colors.primary[700] }}
                >
                  Drag and drop a file here, or
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ textTransform: "none" }}
                >
                  Upload Delivery Note
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleDeliveryNoteSelect}
                  />
                </Button>

                {!deliveryNoteFile && existingDeliveryNoteUrl && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: colors.primary[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      component="a"
                      href={existingDeliveryNoteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ fontWeight: 600, color: colors.primary[400] }}
                    >
                      View current delivery note
                    </Typography>
                  </Box>
                )}

                {deliveryNoteFile && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: colors.primary[800],
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    {deliveryNotePreviewUrl ? (
                      <Box
                        component="img"
                        src={deliveryNotePreviewUrl}
                        alt="Delivery note preview"
                        sx={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 1,
                          bgcolor: colors.primary[700],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {deliveryNoteFile.name.split(".").pop()}
                      </Box>
                    )}

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {deliveryNoteFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(deliveryNoteFile.size / 1024).toFixed(0)} KB
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      color="error"
                      onClick={handleRemoveDeliveryNote}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            sx={{ mt: 3, mb: 3, ml: 1 }}
            onClick={updateDeliveryDetails}
          >
            {isSubmitting ? (
              <CircularProgress color="secondary" size={22} />
            ) : (
              "Update"
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

MemoryBookDelivery.propTypes = {
  setActiveTab: PropTypes.func.isRequired,
};
